import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  FeedbackFilterOptionsInput,
  FeedbackNotificationSettings,
  FeedbackStatsSettings,
  FeedbackSummaryInput,
  ListFeedbackItemsInput,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

export function useFeedbackList(input: ListFeedbackItemsInput) {
  return useQuery({
    ...orpc.core.feedback.list.queryOptions({ input }),
    placeholderData: keepPreviousData,
  });
}

export function useFeedbackItem(id: string | undefined) {
  return useQuery({
    ...orpc.core.feedback.get.queryOptions({ input: { id: id ?? '' } }),
    enabled: !!id,
  });
}

export function useFeedbackSummary(
  input: FeedbackSummaryInput,
  enabled = true,
) {
  return useQuery({
    ...orpc.core.feedback.summary.queryOptions({ input }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Reporter and team choices for the filter bar; Feedback:Update only. */
export function useFeedbackFilterOptions(
  input: FeedbackFilterOptionsInput,
  enabled = true,
) {
  return useQuery({
    ...orpc.core.feedback.filterOptions.queryOptions({ input }),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** People the caller may @mention on a report; Feedback:Read only. */
export function useFeedbackMentionableUsers(
  id: string | undefined,
  enabled = true,
) {
  return useQuery({
    ...orpc.core.feedback.listMentionableUsers.queryOptions({
      input: { id: id ?? '' },
    }),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && !!id,
  });
}

/** The organization's feedback notification settings; Feedback:Read only. */
export function useFeedbackNotificationSettings(enabled = true) {
  return useQuery({
    ...orpc.core.feedback.getNotificationSettings.queryOptions({ input: {} }),
    enabled,
  });
}

export function useUpdateFeedbackNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.core.feedback.updateNotificationSettings.mutationOptions({
      onSuccess: (data: FeedbackNotificationSettings) => {
        queryClient.setQueryData(
          orpc.core.feedback.getNotificationSettings.queryKey({ input: {} }),
          data,
        );
      },
    }),
  );
}

/** The organization's feedback stats settings; Feedback:Read only. */
export function useFeedbackStatsSettings(enabled = true) {
  return useQuery({
    ...orpc.core.feedback.getStatsSettings.queryOptions({ input: {} }),
    enabled,
  });
}

/** Saving a new start date refreshes the stat tiles that read it. */
export function useUpdateFeedbackStatsSettings() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.core.feedback.updateStatsSettings.mutationOptions({
      onSuccess: (data: FeedbackStatsSettings) => {
        queryClient.setQueryData(
          orpc.core.feedback.getStatsSettings.queryKey({ input: {} }),
          data,
        );
        void queryClient.invalidateQueries({
          queryKey: orpc.core.feedback.summary.key(),
        });
      },
    }),
  );
}

export function useFeedbackHistory(id: string | undefined) {
  return useQuery({
    ...orpc.core.feedback.getHistory.queryOptions({
      input: { id: id ?? '' },
    }),
    enabled: !!id,
  });
}

export function useFeedbackComments(id: string | undefined) {
  return useQuery({
    ...orpc.core.feedback.listComments.queryOptions({
      input: { id: id ?? '', limit: 200, offset: 0 },
    }),
    enabled: !!id,
  });
}

function useInvalidateFeedback() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({
      queryKey: orpc.core.feedback.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.core.feedback.summary.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.core.feedback.get.key({ input: { id } }),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.core.feedback.getHistory.key({ input: { id } }),
    });
  };
}

export function useUpdateFeedback() {
  const invalidate = useInvalidateFeedback();
  return useMutation(
    orpc.core.feedback.update.mutationOptions({
      onSuccess: (_data, variables) => invalidate(variables.id),
    }),
  );
}

export function useAddFeedbackComment() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.core.feedback.addComment.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.core.feedback.listComments.key({
            input: { id: variables.id },
          }),
        });
        // The list carries a comment count.
        void queryClient.invalidateQueries({
          queryKey: orpc.core.feedback.list.key(),
        });
      },
    }),
  );
}
